/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import history from "../../../../@history";
import CourseModel from "../model/CourseModel";
import { showMessage } from "../../../store/BASE/messageSlice";

export const getCourse = createAsyncThunk(
  "courses/getCourse",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get course
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/courses/${id}`
      );
      // get response data
      const data = await response.data;
      // return data
      return data;
    } catch (error) {
      // if error, redirect to courses page
      history.push({ pathname: `/courses` });
      // show error message
      dispatch(showMessage({ message: "Course Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const addCourse = createAsyncThunk(
  "courses/addCourse",
  async (course, { dispatch, getState }) => {
    try {
      // set user id
      course.user_id = getState().user.id;
      // send request to add course
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/courses`,
        course
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Course Added" }));
      // return data
      return data;
    } catch (error) {
      // if error, show error message
      dispatch(showMessage({ message: "Course Not Added", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const updateCourse = createAsyncThunk(
  "courses/updateCourse",
  async (course, { dispatch, getState }) => {
    try {
      // set user id
      course.user_id = getState().user.id;
      // send request to update course
      const response = await axios.patch(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/courses/${course.id}`,
        course
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Course Updated" }));
      // return data
      return data;
    } catch (error) { 
      // if error, show error message
      dispatch(showMessage({ message: "Course Not Updated", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const removeCourse = createAsyncThunk(
  "courses/removeCourse",
  async (id, { dispatch, getState }) => {
    try {
      // send request to remove course
      const response = await axios.delete(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/courses/${id}`
      );
      // get response data
      await response.data;
      // show success message
      dispatch(showMessage({ message: "Course Removed" }));
      // return data
      return id;
    } catch (error) {
      // if error, show error message
      dispatch(showMessage({ message: "Course Not Removed", variant: "error" }));
      // return null
      return null;
    }
  }
);

// define selector
export const selectCourse = ({ courses }) => courses.instance;

// define slice
const courseSlice = createSlice({
  name: "course",
  initialState: null,
  reducers: {
    newCourse: (state, action) => CourseModel(),
    resetCourse: () => null,
  },
  extraReducers: {
    [getCourse.pending]: (state, action) => null,
    [getCourse.fulfilled]: (state, action) => action.payload,
    [updateCourse.fulfilled]: (state, action) => action.payload,
    [removeCourse.fulfilled]: (state, action) => null,
  },
});

// export actions
export const { resetCourse, newCourse } = courseSlice.actions;

export default courseSlice.reducer;
